import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import { countryCodes } from '../data/countryCodes';
import { ScrollArea } from '../components/ui/scroll-area';

export default function LeadForm({ packageTitle = 'Essentials', packageAmount = 45, tmSignUpType = 1, isInline = false, onSuccess }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [geoData, setGeoData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setCountryDropdownOpen(false);
        setCountrySearch('');
      }
    };
    if (countryDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [countryDropdownOpen]);

  const fetchGeo = useCallback(async () => {
    const data = await apiService.getGeolocation();
    if (data && data.isSuccess && data.data) {
      setGeoData(data);
      const cc = data.data.countryCode;
      const match = countryCodes.find(c => c.code === cc);
      if (match) setSelectedCountry(match);
      else setSelectedCountry(countryCodes[0]);
    } else {
      setSelectedCountry(countryCodes[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchGeo();
  }, [fetchGeo]);

  const handlePhoneChange = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
    setForm(prev => ({ ...prev, phone: val }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.name.trim()) { setError('Name is required'); return; }
    if (!form.email.trim()) { setError('Email is required'); return; }
    if (!form.phone.trim() || form.phone.length < 7) { setError('Valid phone number is required (7-10 digits)'); return; }

    setLoading(true);
    const pageUrl = window.location.href;
    const ipAddress = geoData?.data?.ipAddress || '';
    const city = geoData?.data?.cityName || '';
    const state = geoData?.data?.regionName || '';
    const geoStr = geoData ? JSON.stringify(geoData) : '';

    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      countryDialCode: selectedCountry?.dial || 1,
      country: selectedCountry?.code || 'US',
      phoneNumber: form.phone,
      ipAddress,
      description: form.message.trim(),
      pageUrl,
      city,
      state,
      geoLocation: geoStr,
      packageTitle: packageTitle || '',
      packageAmount: packageAmount || 0,
      tmSignUpType: tmSignUpType || 1,
    };

    const result = await apiService.submitLead(payload);
    setLoading(false);

    if (result.status === 200 && result.data?.data?.customerId) {
      if (onSuccess) onSuccess();
      window.location.href = `/thanks/?customerid=${result.data.data.customerId}`;
    } else if (result.status === 401) {
      setError(result.data?.message || 'Unauthorized request');
    } else if (result.status === 400) {
      setError(result.data?.errorMessage || 'Invalid input');
    } else {
      setError(result.data?.errorMessage || 'Something went wrong. Please try again.');
    }
  };

  const filteredCountries = countryCodes.filter(c =>
    c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
    c.code.toLowerCase().includes(countrySearch.toLowerCase()) ||
    String(c.dial).includes(countrySearch)
  );

  const inputBase = "bg-[#0A1128] border border-[#1D2D50] text-white placeholder:text-[#A0AABF]/50 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] rounded-sm p-3 w-full transition-colors text-sm font-['Manrope'] outline-none";

  return (
    <form onSubmit={handleSubmit} data-testid="lead-form" className="space-y-4">
      {error && (
        <div data-testid="lead-form-error" className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm p-3 rounded-sm">
          {error}
        </div>
      )}

      <div>
        <input
          data-testid="lead-form-name"
          type="text"
          placeholder="Your Full Name"
          value={form.name}
          onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
          className={inputBase}
        />
      </div>

      <div>
        <input
          data-testid="lead-form-email"
          type="email"
          placeholder="Your Email Address"
          value={form.email}
          onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))}
          className={inputBase}
        />
      </div>

      <div className="flex gap-2">
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            data-testid="lead-form-country-dropdown"
            onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}
            className={`bg-[#0A1128] border text-white rounded-sm p-3 text-sm font-['Manrope'] flex items-center gap-2 min-w-[110px] transition-colors h-full ${countryDropdownOpen ? 'border-[#D4AF37] ring-1 ring-[#D4AF37]' : 'border-[#1D2D50] hover:border-[#D4AF37]/50'}`}
          >
            <span className="text-[#D4AF37] font-semibold">+{selectedCountry?.dial || 1}</span>
            <span className="text-white/40 text-xs">{selectedCountry?.code || 'US'}</span>
            <svg className={`w-3 h-3 text-[#A0AABF] ml-auto transition-transform ${countryDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </button>
          {countryDropdownOpen && (
            <div className="absolute bottom-full left-0 mb-1 w-[280px] bg-[#0F1A35] border border-[#1D2D50] rounded-sm shadow-[0_-10px_40px_rgba(0,0,0,0.4)] z-[200] animate-slide-down overflow-hidden">
              <div className="p-2.5 border-b border-[#1D2D50]">
                <input
                  ref={searchInputRef}
                  data-testid="lead-form-country-search"
                  type="text"
                  placeholder="Search country or code..."
                  value={countrySearch}
                  onChange={e => setCountrySearch(e.target.value)}
                  className="bg-[#0A1128] border border-[#1D2D50] text-white placeholder:text-[#A0AABF]/40 rounded-sm p-2.5 w-full text-sm outline-none focus:border-[#D4AF37] transition-colors"
                />
              </div>
              <ScrollArea className="h-[240px]">
                <div className="py-1">
                  {filteredCountries.length === 0 ? (
                    <p className="text-[#A0AABF]/50 text-xs text-center py-4">No countries found</p>
                  ) : (
                    filteredCountries.map(c => (
                      <button
                        key={`${c.code}-${c.dial}`}
                        type="button"
                        onClick={() => { setSelectedCountry(c); setCountryDropdownOpen(false); setCountrySearch(''); }}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center justify-between gap-2 ${selectedCountry?.code === c.code ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'text-white/70 hover:bg-[#D4AF37]/5 hover:text-white'}`}
                      >
                        <span className="truncate">{c.name}</span>
                        <span className="text-[#D4AF37] text-xs font-semibold flex-shrink-0">+{c.dial}</span>
                      </button>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          )}
        </div>
        <input
          data-testid="lead-form-phone"
          type="tel"
          placeholder="Phone Number"
          value={form.phone}
          onChange={handlePhoneChange}
          maxLength={10}
          className={`${inputBase} flex-1`}
        />
      </div>

      <div>
        <textarea
          data-testid="lead-form-message"
          placeholder="Your Message"
          value={form.message}
          onChange={e => setForm(prev => ({ ...prev, message: e.target.value }))}
          rows={isInline ? 3 : 4}
          className={`${inputBase} resize-none`}
        />
      </div>

      <button
        type="submit"
        data-testid="lead-form-submit"
        disabled={loading}
        className="w-full bg-[#D4AF37] text-[#0A1128] font-bold py-3.5 px-8 rounded-sm transition-all duration-300 hover:bg-[#F3E5AB] hover:-translate-y-0.5 shadow-[0_4px_14px_0_rgba(212,175,55,0.39)] disabled:opacity-50 disabled:cursor-not-allowed text-sm font-['Outfit'] uppercase tracking-wider"
      >
        {loading ? 'Submitting...' : 'Register My Trademark'}
      </button>
    </form>
  );
}
